import * as React from "react"

import { cn } from "@/lib/utils"

import { FINDING_TONE, findingHeadline } from "./severity"
import type { Finding } from "./types"

/**
 * Finding rows share one neutral surface — the section's tinted header carries
 * the severity colour, so repeating it on every row spent the same signal four
 * times over and made the findings read as competing blocks rather than text.
 *
 * Severity survives per row as the leading glyph, which matters because sections
 * genuinely mix severities: a blocked feed's accessibility section carries two
 * critical findings and a warning, and content quality mixes critical, info and
 * skipped. Without the glyph those rows would be indistinguishable.
 */
function CheckRow({ finding }: { finding: Finding }) {
  const tone = FINDING_TONE[finding.severity] ?? FINDING_TONE.info
  const Icon = tone.icon

  /*
   * The glyph trails the text so every headline starts at the same left edge,
   * rather than a column of icons sitting between the card edge and the words a
   * reader is trying to scan.
   *
   * A trailing glyph needs something to sit within or it reads as an orphaned
   * mark — the row's own background is what provides that here. On an unfilled
   * row it would need to lead instead.
   */
  return (
    <li className="tw:flex tw:items-start tw:gap-3 tw:rounded-md tw:bg-muted/40 tw:px-3 tw:py-2.5">
      <div className="tw:min-w-0 tw:flex-1">
        <div className="tw:text-sm tw:font-semibold tw:text-foreground">
          {findingHeadline(finding)}
        </div>
        {finding.detail && (
          <div className="tw:mt-0.5 tw:text-sm tw:text-muted-foreground">{finding.detail}</div>
        )}
      </div>
      {/* role="img" is what makes the label announce: severity is carried only by
          the glyph's shape and colour, so an unlabelled svg would drop the
          distinction entirely for anyone not seeing it, and aria-label alone is
          not reliably exposed on a bare svg. */}
      <Icon
        role="img"
        aria-label={finding.severity}
        className={cn("tw:mt-0.5 tw:size-4 tw:shrink-0", tone.text)}
      />
    </li>
  )
}

export default function MedicCheckList({
  findings,
  className,
}: {
  findings?: Finding[]
  className?: string
}) {
  if (!findings || findings.length === 0) return null

  return (
    <ul className={cn("tw:flex tw:flex-col tw:gap-1.5", className)}>
      {findings.map((finding, idx) => (
        <CheckRow key={`${finding.key}-${idx}`} finding={finding} />
      ))}
    </ul>
  )
}
